import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { IDLE_TIMEOUT_MS } from '@/config/session';
import { idleLockTakesOver } from '@/components/Tech/PinLock';

// User-interaction signals that count as "activity" and reset the idle timer.
// NOTE: we intentionally do NOT reset on raw API calls — the app has background
// polling (notifications, chat, heatmaps via refetchInterval), so treating every
// request as activity would keep an idle-but-open tab alive forever. Genuine
// user-initiated requests are always accompanied by one of these events anyway.
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

/**
 * Logs the user out after IDLE_TIMEOUT_MS of no activity. Mount once in the
 * authenticated app shell. Resets on mouse/keyboard/scroll/touch/click and on
 * route navigation (location change re-runs the effect). Does nothing while the
 * user is unauthenticated.
 */
export function useIdleLogout(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const timer = useRef<number | undefined>(undefined);
  const lastReset = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fireLogout = () => {
      // FM Technician B17: a technician with a PIN is LOCKED instead (the PIN
      // lock screen has already covered the app); never signed out mid-shift.
      if (idleLockTakesOver(useAuthStore.getState().user?.id)) return;
      logout();
      queryClient.clear();
      navigate('/login', { replace: true });
    };

    const reset = () => {
      const now = Date.now();
      // mousemove/scroll fire rapidly — re-arm at most once/sec.
      if (now - lastReset.current < 1000) return;
      lastReset.current = now;
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(fireLogout, IDLE_TIMEOUT_MS);
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    // Arm immediately (and on every navigation, since location.pathname is a dep).
    lastReset.current = 0;
    reset();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [isAuthenticated, logout, navigate, queryClient, location.pathname]);
}
